import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import Card from '../components/Card'
import Button from '../components/Button'
import Input from '../components/Input'
import Select from '../components/Select'
import {
  ArrowLeft,
  Building2,
  Users,
  DollarSign,
  Calendar,
  Settings,
  UserCheck,
  FileText,
  Heart,
} from 'lucide-react'
import { getOrganizationDetails, updateSubscriptionTier, updateBillingCycle, updateMemberRole } from '../services/masterAdmin'
import { getBillingSummary, getBillingCycles } from '../services/billingService'
import { getInvoices } from '../services/invoiceService'
import { getSubscriptionTiers } from '../services/featureGating'
import { getBillingCycleInfo } from '../services/billingCycleService'
import { getOrganizationLimits, setOrganizationLimits } from '../services/organizationLimits'
import { supabase } from '../services/supabase'
import { formatDate } from '../utils/helpers'

const MasterAdminOrganizationDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [organization, setOrganization] = useState(null)
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)
  const [billingSummary, setBillingSummary] = useState(null)
  const [availableTiers, setAvailableTiers] = useState([])
  const [billingCycleInfo, setBillingCycleInfo] = useState(null)
  const [orgLimits, setOrgLimits] = useState(null)
  const [limitsForm, setLimitsForm] = useState({
    maxAdmins: '',
    maxUsers: '',
  })
  const [updatingLimits, setUpdatingLimits] = useState(false)
  const [tierForm, setTierForm] = useState({
    tier: 'demo',
    trialEndsAt: '',
    billingCycle: 'monthly',
  })
  const [updatingCycle, setUpdatingCycle] = useState(false)
  const [updatingMemberRole, setUpdatingMemberRole] = useState(null)
  const [selectedMember, setSelectedMember] = useState(null)

  useEffect(() => {
    if (id) {
      loadOrganization()
    }
  }, [id])

  // Load tiers - will be called when organization changes to include current tier
  useEffect(() => {
    loadTiers()
  }, [organization?.subscription_tier])

  const loadTiers = async () => {
    try {
      // Fetch all subscription tiers from database (including inactive ones for master admin)
      const { data: tiers, error } = await supabase
        .from('subscription_tiers')
        .select('*')
        .order('price_monthly', { ascending: true })

      if (error) throw error

      // Convert to options format
      let tierOptions = (tiers || []).map(tier => ({
        value: tier.code,
        label: tier.name,
        tier: tier, // Keep full tier object for reference
      }))

      // If organization has a tier that's not in the list (e.g., old/inactive tier),
      // add it to the options so it can still be displayed/selected
      const currentTierCode = organization?.subscription_tier || tierForm.tier
      if (currentTierCode) {
        const tierExists = tierOptions.some(t => t.value === currentTierCode)
        
        if (!tierExists) {
          // Add the current tier even if it's not in the database (for backward compatibility)
          const formattedLabel = currentTierCode
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
          tierOptions.push({
            value: currentTierCode,
            label: formattedLabel,
          })
        }
      }

      setAvailableTiers(tierOptions)
    } catch (error) {
      console.error('Error loading subscription tiers:', error)
      // Fallback to hardcoded options if database fetch fails
      setAvailableTiers([
        { value: 'demo', label: 'Demo' },
        { value: 'lite', label: 'Lite' },
        { value: 'essential', label: 'Essential' },
        { value: 'professional', label: 'Professional' },
        { value: 'enterprise', label: 'Enterprise' },
        { value: 'white_label', label: 'White Label' },
      ])
    }
  }

  const loadOrganization = async () => {
    try {
      setLoading(true)
      const data = await getOrganizationDetails(id)
      setOrganization(data)
      
      // Load billing cycle info
      const cycleInfo = await getBillingCycleInfo(id)
      if (cycleInfo.success && cycleInfo.data) {
        setBillingCycleInfo(cycleInfo.data)
        setTierForm({
          tier: data.subscription_tier || 'demo',
          trialEndsAt: data.trial_ends_at
            ? new Date(data.trial_ends_at).toISOString().slice(0, 16)
            : '',
          billingCycle: cycleInfo.data.currentCycle || 'monthly',
        })
      } else {
        setTierForm({
          tier: data.subscription_tier || 'demo',
          trialEndsAt: data.trial_ends_at
            ? new Date(data.trial_ends_at).toISOString().slice(0, 16)
            : '',
          billingCycle: 'monthly',
        })
      }
      
      // Load billing summary
      const summary = await getBillingSummary(id)
      if (summary.success) {
        setBillingSummary(summary.data)
      }

      // Load organization limits
      const limits = await getOrganizationLimits(id)
      if (limits.success && limits.data) {
        setOrgLimits(limits.data)
        setLimitsForm({
          maxAdmins: limits.data.max_admins?.toString() || '',
          maxUsers: limits.data.max_users?.toString() || '',
        })
      }
    } catch (error) {
      console.error('Error loading organization:', error)
      alert('Failed to load organization details')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateLimits = async () => {
    if (!organization) return
    try {
      setUpdatingLimits(true)
      const limits = {
        maxAdmins: limitsForm.maxAdmins ? parseInt(limitsForm.maxAdmins) : null,
        maxUsers: limitsForm.maxUsers ? parseInt(limitsForm.maxUsers) : null,
      }
      const result = await setOrganizationLimits(organization.id, limits)
      if (result.success) {
        await loadOrganization()
        alert('Organization limits updated successfully!')
      } else {
        throw new Error(result.error || 'Failed to update limits')
      }
    } catch (error) {
      console.error('Error updating limits:', error)
      alert(error.message || 'Failed to update organization limits')
    } finally {
      setUpdatingLimits(false)
    }
  }

  const handleClearCustomLimits = async () => {
    if (!organization) return
    if (!confirm('Are you sure you want to clear custom limits? The organization will use tier defaults.')) {
      return
    }
    try {
      setUpdatingLimits(true)
      const result = await setOrganizationLimits(organization.id, {}, true)
      if (result.success) {
        await loadOrganization()
        alert('Custom limits cleared. Organization now uses tier defaults.')
      } else {
        throw new Error(result.error || 'Failed to clear limits')
      }
    } catch (error) {
      console.error('Error clearing limits:', error)
      alert(error.message || 'Failed to clear organization limits')
    } finally {
      setUpdatingLimits(false)
    }
  }

  const handleUpdateTier = async () => {
    if (!organization) return
    try {
      setUpdating(true)
      await updateSubscriptionTier(organization.id, tierForm.tier, tierForm.trialEndsAt || null)
      // Reload organization to get updated data
      await loadOrganization()
      alert('Subscription tier updated successfully! The changes will be reflected throughout the application.')
    } catch (error) {
      console.error('Error updating tier:', error)
      alert(error.message || 'Failed to update subscription tier')
    } finally {
      setUpdating(false)
    }
  }

  const handleUpdateBillingCycle = async () => {
    if (!organization) return
    try {
      setUpdatingCycle(true)
      const result = await updateBillingCycle(organization.id, tierForm.billingCycle)
      if (result.success) {
        await loadOrganization()
        alert(`Billing cycle updated to ${tierForm.billingCycle}. Changes will take effect at the end of the current billing period.`)
      } else {
        throw new Error(result.error || 'Failed to update billing cycle')
      }
    } catch (error) {
      console.error('Error updating billing cycle:', error)
      alert(error.message || 'Failed to update billing cycle')
    } finally {
      setUpdatingCycle(false)
    }
  }

  const handleUpdateMemberRole = async (memberId, newRole) => {
    if (!organization) return
    try {
      setUpdatingMemberRole(memberId)
      await updateMemberRole(organization.id, memberId, newRole)
      await loadOrganization()
      alert(`Member role updated successfully to ${newRole}.`)
    } catch (error) {
      console.error('Error updating member role:', error)
      alert(error.message || 'Failed to update member role')
    } finally {
      setUpdatingMemberRole(null)
    }
  }

  if (loading) {
    return (
      <div className="section-container">
        <div className="text-center py-12">
          <p className="text-gray-600">Loading organization details...</p>
        </div>
      </div>
    )
  }

  if (!organization) {
    return (
      <div className="section-container">
        <p className="text-red-500">Organization not found.</p>
        <Button className="mt-4" onClick={() => navigate('/master-admin/organizations')}>
          Back to Organizations
        </Button>
      </div>
    )
  }

  return (
    <div className="section-container space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="secondary" onClick={() => navigate('/master-admin/organizations')}>
          <ArrowLeft size={18} className="mr-2" />
          Back
        </Button>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
            <Building2 size={24} className="text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{organization.name}</h1>
            <p className="text-gray-600">Organization Details & Management</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Organization Info */}
          <Card variant="soft" className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Organization Information</h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Name</p>
                <p className="font-semibold text-gray-900 dark:text-white">{organization.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Slug</p>
                <p className="font-semibold text-gray-900 dark:text-white">{organization.slug}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Created</p>
                <p className="font-semibold text-gray-900 dark:text-white">{formatDate(organization.created_at)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Subscription Tier</p>
                <p className="font-semibold text-gray-900 dark:text-white capitalize">
                  {availableTiers.find(t => t.value === organization.subscription_tier)?.label || organization.subscription_tier || 'Demo'}
                </p>
              </div>
            </div>
          </Card>

          {/* Billing Summary */}
          {billingSummary && (
          <Card variant="soft" className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <DollarSign size={20} />
              Billing Summary
            </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Total Revenue</p>
                  <p className="text-xl font-bold text-green-600">
                    ${billingSummary.total_revenue?.toLocaleString() || 0}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Pending</p>
                  <p className="text-xl font-bold text-yellow-600">
                    ${billingSummary.pending_amount?.toLocaleString() || 0}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Overdue</p>
                  <p className="text-xl font-bold text-red-600">
                    ${billingSummary.overdue_amount?.toLocaleString() || 0}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Invoices</p>
                  <p className="text-xl font-bold">{billingSummary.total_invoices || 0}</p>
                </div>
              </div>
            </Card>
          )}

          {/* Members */}
          <Card variant="soft" className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Users size={20} />
              Organization Members ({organization.organization_members?.length || 0})
            </h2>
            {organization.organization_members && organization.organization_members.length > 0 ? (
              <div className="space-y-3">
                {organization.organization_members.map((member) => {
                  const userDetails = member.user_details
                  const isLastAdmin = organization.organization_members.filter(m => m.role === 'admin').length === 1 && member.role === 'admin'
                  
                  return (
                    <div
                      key={member.id}
                      className="p-4 rounded-xl bg-white/60 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <p className="font-semibold text-gray-900 dark:text-white">
                              {userDetails?.name || userDetails?.email || 'Unknown User'}
                            </p>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${
                              member.role === 'admin'
                                ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400'
                                : member.role === 'practitioner'
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                            }`}>
                              {member.role}
                            </span>
                            {isLastAdmin && (
                              <span className="px-2 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400">
                                Last Admin
                              </span>
                            )}
                          </div>
                          {userDetails && (
                            <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                              <p><strong>Email:</strong> {userDetails.email}</p>
                              {userDetails.name && userDetails.name !== userDetails.email && (
                                <p><strong>Name:</strong> {userDetails.name}</p>
                              )}
                              <p><strong>Joined:</strong> {formatDate(member.joined_at)}</p>
                              {userDetails.created_at && (
                                <p><strong>Account Created:</strong> {formatDate(userDetails.created_at)}</p>
                              )}
                              {userDetails.last_sign_in_at && (
                                <p><strong>Last Sign In:</strong> {formatDate(userDetails.last_sign_in_at)}</p>
                              )}
                              {userDetails.email_confirmed_at ? (
                                <p className="text-green-600 dark:text-green-400">✓ Email Confirmed</p>
                              ) : (
                                <p className="text-yellow-600 dark:text-yellow-400">⚠ Email Not Confirmed</p>
                              )}
                            </div>
                          )}
                          {!userDetails && (
                            <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                              <p><strong>Joined:</strong> {formatDate(member.joined_at)}</p>
                              <p className="text-yellow-600 dark:text-yellow-400 text-xs">User details not available</p>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <Select
                            value={member.role}
                            onChange={(e) => handleUpdateMemberRole(member.id, e.target.value)}
                            options={[
                              { value: 'admin', label: 'Admin' },
                              { value: 'practitioner', label: 'Practitioner' },
                              { value: 'viewer', label: 'Viewer' },
                              { value: 'member', label: 'Member' },
                            ]}
                            disabled={updatingMemberRole === member.id}
                            className="w-40 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          />
                          {updatingMemberRole === member.id && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">Updating...</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">No members yet</p>
            )}
          </Card>
        </div>

        {/* Subscription Management */}
        <div className="space-y-6">
          <Card variant="soft" className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Settings size={20} />
              Subscription Management
            </h2>
            <div className="space-y-4">
              <Select
                label="Subscription Tier"
                options={availableTiers}
                value={tierForm.tier}
                onChange={(e) => setTierForm({ ...tierForm, tier: e.target.value })}
                className="w-full"
                placeholder="Select subscription tier"
              />
              <Input
                type="datetime-local"
                label="Trial Ends At (optional)"
                value={tierForm.trialEndsAt}
                onChange={(e) => setTierForm({ ...tierForm, trialEndsAt: e.target.value })}
              />
              <Button
                onClick={handleUpdateTier}
                disabled={updating}
                className="w-full"
              >
                {updating ? 'Updating...' : 'Update Subscription'}
              </Button>
            </div>
          </Card>

          {/* Billing Cycle Management */}
          <Card variant="soft" className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Calendar size={20} />
              Billing Cycle
            </h2>
            {billingCycleInfo ? (
              <div className="space-y-4">
                {/* Current Billing Cycle Display */}
                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Current Cycle</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      billingCycleInfo.currentCycle === 'yearly'
                        ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400'
                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                    }`}>
                      {billingCycleInfo.currentCycle === 'yearly' ? 'Yearly' : 'Monthly'}
                    </span>
                  </div>
                  <div className="space-y-2 mt-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Current Period</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {formatDate(billingCycleInfo.currentPeriodStart)} - {formatDate(billingCycleInfo.currentPeriodEnd)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
                        <Calendar size={14} />
                        Next Renewal
                      </span>
                      <span className="font-semibold text-primary-600 dark:text-primary-400">
                        {formatDate(billingCycleInfo.nextRenewalDate)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Billing Cycle Selector */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Change Billing Cycle
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setTierForm({ ...tierForm, billingCycle: 'monthly' })}
                      className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
                        tierForm.billingCycle === 'monthly'
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-semibold">Monthly</div>
                      <div className="text-xs mt-1">
                        R{billingCycleInfo.monthlyPrice?.toLocaleString() || '0'}/month
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setTierForm({ ...tierForm, billingCycle: 'yearly' })}
                      className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
                        tierForm.billingCycle === 'yearly'
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-semibold">Yearly</div>
                      <div className="text-xs mt-1">
                        R{billingCycleInfo.yearlyPrice?.toLocaleString() || '0'}/year
                      </div>
                      {billingCycleInfo.yearlyPrice && billingCycleInfo.monthlyPrice && (
                        <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                          Save {Math.round((1 - (billingCycleInfo.yearlyPrice / 12) / billingCycleInfo.monthlyPrice) * 100)}%
                        </div>
                      )}
                    </button>
                  </div>
                </div>

                {/* Change Preview */}
                {tierForm.billingCycle !== billingCycleInfo.currentCycle && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      <strong>Change will take effect:</strong> {formatDate(billingCycleInfo.currentPeriodEnd)}
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      Your next renewal date will be updated after the current period ends.
                    </p>
                  </div>
                )}

                <Button
                  onClick={handleUpdateBillingCycle}
                  disabled={updatingCycle || tierForm.billingCycle === billingCycleInfo.currentCycle}
                  className="w-full"
                >
                  {updatingCycle ? 'Updating...' : 'Update Billing Cycle'}
                </Button>
              </div>
            ) : (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Loading billing cycle information...
              </div>
            )}
          </Card>

          {/* Stats */}
          <Card variant="soft" className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Quick Stats</h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                  <UserCheck size={18} />
                  <span>Clients</span>
                </div>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {organization.clients?.[0]?.count || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                  <Users size={18} />
                  <span>Members</span>
                </div>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {organization.organization_members?.length || 0}
                </span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default MasterAdminOrganizationDetails

